import { isIgnoredToken } from '@/utils/ignoredTokens'
import { withConfirmed, type ConfirmedToken, type SplitHints, type SubjectRef } from '@/utils/splitSuggestion'
import { assignedSplit } from '@/utils/tokenSplit'

const SHOP_FIELDS = ['subject', 'room', 'class_name'] as const

export interface ShopCatalogRow {
  id: string
  name: string
  code?: string | null
  aliases?: string[]
}

export interface ShopSplitPart {
  token: string
  field: string
}

function labelsOf(row: ShopCatalogRow): string[] {
  return [row.name, row.code, ...(row.aliases || [])].map((value) => (value || '').trim()).filter(Boolean)
}

export function hintsForShop(
  hints: SplitHints,
  targets: Record<string, ShopCatalogRow[]>,
  ignored: string[],
): SplitHints {
  const confirmed: ConfirmedToken[] = []
  const subjects: string[] = []
  const subjectRefs: SubjectRef[] = []
  for (const field of SHOP_FIELDS) {
    for (const row of targets[field] || []) {
      const labels = labelsOf(row)
      for (const label of labels) confirmed.push({ text: label, field })
      if (field === 'subject' && row.id && labels.length) {
        subjects.push(...labels)
        subjectRefs.push({ id: row.id, labels })
      }
    }
  }
  for (const name of ignored) {
    const text = name.trim()
    if (text) confirmed.push({ text, field: 'ignore' })
  }
  const merged = withConfirmed(hints, confirmed)
  return {
    ...merged,
    subjects: [...(merged.subjects || []), ...subjects],
    subjectRefs: [...(merged.subjectRefs || []), ...subjectRefs],
  }
}

export function planShopAutoSplit(
  token: string,
  sourceField: string,
  hints: SplitHints,
  ignored: string[],
): ShopSplitPart[] | null {
  const split = assignedSplit(token, hints)
  if (!split || split.parts.length < 2 || split.parts.length !== split.fields.length) return null
  const fields = split.parts.map((part, index) => (
    isIgnoredToken(part, ignored) ? 'ignore' : (split.fields[index] || '')
  ))
  const known = fields.filter((field) => field && field !== 'ignore')
  if (new Set(known).size !== known.length) return null
  const used = new Set(known)
  const empties = fields.map((field, index) => (field ? -1 : index)).filter((index) => index >= 0)
  if (empties.length > 1) {
    const free = SHOP_FIELDS.filter((field) => !used.has(field))
    const ordered = free.includes(sourceField as typeof SHOP_FIELDS[number])
      ? [sourceField, ...free.filter((field) => field !== sourceField)]
      : [...free]
    empties.forEach((index, offset) => {
      const pick = ordered[offset]
      if (pick) {
        fields[index] = pick
        used.add(pick)
        return
      }
      fields[index] = 'open'
    })
  } else if (empties.length === 1) {
    const free = SHOP_FIELDS.filter((field) => !used.has(field))
    const sourceFree = free.includes(sourceField as typeof SHOP_FIELDS[number])
    const extracting = used.has('room') || used.has('class_name')
    const pick = sourceFree ? sourceField : extracting ? free[0] : ''
    if (!pick && !extracting) return null
    if (pick) {
      fields[empties[0]!] = pick
      used.add(pick)
    } else {
      fields[empties[0]!] = 'open'
    }
  }
  if (fields.some((field) => !field)) return null
  const placed = fields.filter((field) => field !== 'ignore' && field !== 'open')
  if (new Set(placed).size !== placed.length) return null
  return split.parts.map((part, index) => ({ token: part, field: fields[index]! }))
}
